import {isDeepEqual} from '@raison/toolbox';
import {locale} from '~/config';
import {Amount} from './Amount';

abstract class MoneyFormatMode<V> {
    abstract getNumber(value: V): number;
}

class KnowsAmountMode extends MoneyFormatMode<void> {
    constructor(private readonly amount: MoneyAmount) {
        super();
    }

    getNumber(): number {
        return this.amount.toNumber();
    }
}

class NeedsAmountMode extends MoneyFormatMode<MoneyFormat.Value> {
    getNumber(value: MoneyFormat.Value): number {
        if (value instanceof MoneyAmount) {
            return value.toNumber();
        } else if (typeof value === 'number') {
            return value;
        } else {
            return parseFloat(value);
        }
    }
}

/**
 * @deprecated @see ~/entities/currencies useCurrencyFormat
 */
export class MoneyFormat<T extends Currency | MoneyAmount> {
    static from<T extends Currency | MoneyAmount>(money: T, options?: MoneyFormat.Options) {
        const {code, decimals} = money;

        let mode: MoneyFormatMode<MoneyFormat.Value | void>;
        if (money instanceof MoneyAmount) {
            mode = new KnowsAmountMode(money);
        } else {
            mode = new NeedsAmountMode();
        }

        const iso4217Currencies = new Set(Intl.supportedValuesOf('currency'));

        let numberFormat: Intl.NumberFormat;
        if (iso4217Currencies.has(code.toUpperCase())) {
            numberFormat = new Intl.NumberFormat(locale, {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
                ...options,
                currency: code,
                currencyDisplay: 'narrowSymbol',
                style: 'currency',
            });
        } else {
            numberFormat = new Intl.NumberFormat(locale, {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
                ...options,
            });
        }
        return new MoneyFormat(mode, numberFormat, {code, decimals});
    }

    private constructor(
        private readonly mode: MoneyFormatMode<MoneyFormat.Value | void>,
        private readonly numberFormat: Intl.NumberFormat,
        private readonly money: Currency
    ) {}

    format(value: MoneyFormat.ValueArg<T>) {
        return this.formatWithSymbol(value);
    }

    localeFormat(value: MoneyFormat.ValueArg<T>) {
        return this.numberFormat.format(this.toNumber(value));
    }

    formatWithSymbol(value: MoneyFormat.ValueArg<T>) {
        const parts = this.formatToParts(value);
        if (parts.symbol === undefined || parts.symbol === this.money.code) {
            return parts.amount;
        } else {
            return `${parts.symbol}${parts.amount}`;
        }
    }

    formatWithCode(value: MoneyFormat.ValueArg<T>) {
        return this.formatWithParts(value, parts => `${parts.amount} ${parts.code}`);
    }

    formatWithParts(value: MoneyFormat.ValueArg<T>, formatter: (parts: MoneyFormat.Parts) => string) {
        return formatter(this.formatToParts(value));
    }

    formatAsNumber(value: MoneyFormat.ValueArg<T>) {
        return this.formatWithParts(value, parts => parts.amount);
    }

    formatToParts(value: MoneyFormat.ValueArg<T>): MoneyFormat.Parts {
        const parts = this.numberFormat.formatToParts(this.toNumber(value));
        const symbolPartIndex = parts.findIndex(part => part.type === 'currency');
        const values = parts.map(part => part.value);
        let symbol: string | undefined = undefined;
        if (symbolPartIndex > -1) {
            symbol = values.at(symbolPartIndex);
            values.splice(symbolPartIndex, 1);
        }
        return {
            amount: values.join(''),
            symbol,
            code: this.money.code,
        };
    }

    private toNumber(value: MoneyFormat.ValueArg<T>): number {
        if (value instanceof MoneyAmount) {
            this.assertSameCurrency(value);
        }
        return this.mode.getNumber(value);
    }

    private assertSameCurrency(amount: MoneyAmount) {
        if (amount.isOfUnit(this.money)) {
            return;
        }
        const amountCurrency = JSON.stringify(amount.toUnit());
        const thisCurrency = JSON.stringify(this.money);
        throw new TypeError(
            `Can't format MoneyAmount of different currency, got ${amountCurrency}, expected ${thisCurrency}`
        );
    }
}

export namespace MoneyFormat {
    export type Value = Amount.Value<never, MoneyAmount>;
    export type ValueArg<T extends Currency | MoneyAmount> = T extends MoneyAmount ? void : MoneyFormat.Value;
    export type Parts = {symbol: string | undefined; amount: string; code: string};
    export type Options = Omit<Intl.NumberFormatOptions, 'currency' | 'currencyDisplay' | 'style'>;
}

export class MoneyAmount extends Amount<Currency> implements Currency {
    static from(value: Amount.Value<Currency>, currency: Currency) {
        return new MoneyAmount(value, currency);
    }

    public readonly code: string;
    public readonly decimals: number;

    private constructor(value: Amount.Value<Currency>, currency: Currency) {
        super(value, currency, isDeepEqual);
        this.code = currency.code;
        this.decimals = currency.decimals;
    }
}

export class Currency {
    static from(code: string, decimals: number) {
        return new Currency(code, decimals);
    }

    private constructor(
        public readonly code: string,
        public readonly decimals: number
    ) {}
}
